import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '../../store';
import Leads from '../Leads';
import AiMarketing from '../AiMarketing';
import AIWReports from '../AIWReports';
import { brandOr } from '../../utils/brand';
import { SOCIAL_POSTER_URL as SOCIAL_URL } from '../../modules';   // this installation's Social Poster address ('' = not set)

// Panda Marketing = the firm's growth hub: Leads + AI Marketing + Social Poster + AI Working Reports,
// all under one page. Each tab keeps its own permission key, so a user only sees the tabs they can open.
type PMTab = 'leads' | 'ai_marketing' | 'social_poster' | 'compliance';

export default function PandaMarketing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me } = useStore();
  const [params, setParams] = useSearchParams();

  const can = (k: PMTab) => me?.role === 'admin' || (me?.modules || []).includes(k);
  const TABS: { key: PMTab; label: string }[] = [
    { key: 'leads', label: t('mod_leads') },
    { key: 'ai_marketing', label: t('mod_ai_marketing') },
    { key: 'social_poster', label: t('mod_social_poster') },
    { key: 'compliance', label: t('mod_compliance') },
  ];
  const visible = TABS.filter(x => can(x.key));
  const param = params.get('v') as PMTab | null;
  const [tab, setTab] = useState<PMTab>(param && visible.some(v => v.key === param) ? param : (visible[0]?.key || 'leads'));
  const select = (k: PMTab) => { setTab(k); setParams(p => { const n = new URLSearchParams(p); n.set('v', k); return n; }, { replace: true }); };

  const tabBtn = (k: PMTab, label: string) => (
    <button key={k} onClick={() => select(k)} style={{ padding: '8px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'transparent', color: tab === k ? 'var(--accent)' : 'var(--text-tertiary)', borderBottom: `3px solid ${tab === k ? 'var(--accent)' : 'transparent'}` }}>{label}</button>
  );

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_panda_marketing')}</div>
      </header>

      <div style={{ display: 'flex', gap: 4, padding: '0 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        {visible.map(v => tabBtn(v.key, v.label))}
      </div>

      {tab === 'leads' ? <Leads embedded />
        : tab === 'ai_marketing' ? <AiMarketing embedded />
        : tab === 'compliance' ? <AIWReports embedded />
        : (
          // Social Poster is a separate app (slf.pandabot.ai). It CANNOT be logged into inside a
          // cross-site iframe — the browser drops its session cookie as a third-party cookie, so the
          // login silently fails. So we launch it in a new tab (where cookies are first-party) instead
          // of embedding a login form that can't work.
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, textAlign: 'center' }}>
            <div style={{ width: 68, height: 68, borderRadius: 18, background: 'var(--accent-bg, #ECFDF5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-speakerphone" style={{ fontSize: 34, color: 'var(--accent)' }} aria-hidden />
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>{t('mod_social_poster')}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 420, lineHeight: 1.6 }}>{t('pm_social_launch_hint')}</div>
            {SOCIAL_URL ? (<>
              <a href={SOCIAL_URL} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '11px 22px', borderRadius: 10, background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
                <i className="ti ti-external-link" style={{ fontSize: 16 }} aria-hidden />{t('pm_social_open')}
              </a>
              <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{SOCIAL_URL}</span>
            </>) : (
              // No address configured for this installation (.env.production REACT_APP_SOCIAL_POSTER_URL): say so
              // instead of a button that goes nowhere.
              <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)', maxWidth: 420, lineHeight: 1.6 }}>{t('fc_social_unset')}</span>
            )}
          </div>
        )}
    </div>
  );
}
